import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### アンケート API 操作クラス
 */
export default class Questionnaires {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET Questionnaire
   */
  public async get(userStore: LoginUser, questionnaireId: number): Promise<QuestionnairesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      questionnaireId: questionnaireId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Questionnaire, formatParams);
    const response = await this.commonService.executeGet<QuestionnairesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET Questionnaires
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number }): Promise<Array<QuestionnairesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Questionnaires, formatParams);
    const response = await this.commonService.executeGet<Array<QuestionnairesGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET QuestionnaireCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Questionnaires, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### GET QuestionnaireAnswers
   */
  public async getAnswers(userStore: LoginUser, questionnaireId: number): Promise<Array<QuestionnaireAnswersGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      questionnaireId: questionnaireId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.QuestionnaireAnswers, formatParams);
    const response = await this.commonService.executeGet<Array<QuestionnaireAnswersGetModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### POST Questionnaire
   */
  public async post(userStore: LoginUser, requestBody: QuestionnairesPostModel): Promise<QuestionnairesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Questionnaires, formatParams);
    const response = await this.commonService.executePost<QuestionnairesGetModel>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH Questionnaire
   */
  public async patch(userStore: LoginUser, questionnaireId: number, requestBody: QuestionnairesPatchModel): Promise<QuestionnairesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      questionnaireId: questionnaireId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Questionnaire, formatParams);
    const response = await this.commonService.executePatch<QuestionnairesGetModel>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE Questionnaire
   */
  public async delete(userStore: LoginUser, questionnaireId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      questionnaireId: questionnaireId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Questionnaire, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * RESEND Questionnaire
   */
  public async reSend(userStore: LoginUser, questionnaireId: number, requestBody: ResendQuestionnairesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      questionnaireId: questionnaireId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ResendQuestionnaire, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface QuestionnairesGetModel {
  /** 施設ID */
  facilityId: number;
  /** 部屋を示す ID */
  id: number;
  /** アンケートタイトル */
  title: string;
  /** アンケート説明文 */
  text: string;
  /** 説明文用の添付ファイル */
  fileUrls: Array<FileUrl>;
  /** 匿名にするか否か */
  hidden: boolean;
  /** メッセージタイトル */
  messageTitle: string;
  /** メッセージ本文 */
  messageText: string;
  /** 宛先 */
  targets: string[];
  /** 宛先(LINE) */
  lineTargets: string[];
  /** 宛先(MAIL) */
  mailTargets: string[];
  /** 宛先グループ情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo>;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo>;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<OtherUserGroupInfo>;
  /** 宛先種別 */
  targetType: TargetType;
  /** 履歴 */
  histories: Array<QuestionnaireHistory>;
  /** アンケート内容 */
  questions: Array<QuestionTemplateItem>;
  /** 送信した messages のソートキー */
  messagesSortKey: string;
  /** 回答受付中か否か */
  acceptingAnswers: boolean;
  /** 期日 */
  replyLimit: number;
  /** 送信者ID */
  sender: string;
  /** 送信者名 */
  senderName: string;
  /** 最終送信日時 */
  sendDatetime: number;
  /** 共有一般ユーザーID */
  shareStandardUsers: string[];
  /** 作成者 */
  writer: string;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface FileUrl {
  /** ファイル名 */
  name: string;
  /** ファイル種類 */
  type: string;
  /** ファイルURL */
  url: string;
}

export enum TargetType {
  /** 家族全員 */
  ALL_FAMILIES = "allFamilies",
  /** 家族個別 */
  FAMILIES = "families",
  /** 施設関係者個別 */
  OTHER_USERS = "otherUsers",
  /** 宛先グループ */
  SEND_MESSAGE_GROUPS = "sendMessageGroups",
  /** 利用者グループ */
  RESIDENT_GROUPS = "residentGroups",
  /** 施設関係者グループ */
  OTHER_USER_GROUPS = "otherUserGroups"
}

export interface QuestionnaireHistory {
  /** メッセージタイトル */
  messageTitle: string;
  /** メッセージ本文 */
  messageText: string;
  /** 宛先 */
  targets: string[];
  /** 宛先(LINE) */
  lineTargets: string[];
  /** 宛先(MAIL) */
  mailTargets: string[];
  /** 宛先グループ情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo>;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo>;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<OtherUserGroupInfo>;
  /** 送信した messages のソートキー */
  messagesSortKey: string;
  /** 送信者ID */
  sender: string;
  /** 送信者名 */
  senderName: string;
  /** 最終送信日時 */
  sendDatetime: number;
}

export interface SendMessageGroupInfo {
  /** 宛先グループソートキー */
  sendMessageGroupsSortKey: string;
  /** 宛先グループID */
  id: number;
  /** 宛先グループ名 */
  name: string;
  /** 送信宛先種別 */
  type: GroupType;
  /** 宛先グループメンバーのソートキー */
  memberSortKeys: string[];
}

export interface ResidentGroupInfo {
  /** ID */
  id: number;
  /** グループ名 */
  name: string;
  /** 利用者グループに所属する利用者家族のソートキー */
  residentsSortKeys: string[];
}

export interface OtherUserGroupInfo {
  /** 施設関係者グループソートキー */
  otherUserGroupsSortKey: string;
  /** グループID */
  id: number;
  /** グループ名 */
  name: string;
  /** 施設関係者グループメンバーのソートキー */
  memberSortKeys: string[];
}

export enum GroupType {
  // 画面バリデーションで使用するので、初期値としてい使用する場合は空文字を格納する
  UNKNOWN = "",
  FAMILY = 0,
  OTHER_USER = 1
}

export interface QuestionTemplateItem {
  /** テンプレート種別 */
  type: TemplateType;
  /** 見出し(最大 100 文字) */
  title: string;
}

export enum TemplateType {
  TEXT = "text",
  SELECT = "select"
}

export interface QuestionnaireTextItem extends QuestionTemplateItem {
  /** 回答入力用のプロパティ。回答内容(最大 1000 文字) */
  answer: string;
  /** 長い回答か否か */
  isLong: boolean;
  /** 必須入力か否か */
  required: boolean;
}

export interface QuestionnaireSelectItem extends QuestionTemplateItem {
  /** 回答入力用のプロパティ。回答内容の配列 */
  answer: string[];
  /** 質問内容の配列 */
  options: Array<QuestionnaireSelectItemOption>;
  /** 複数回答可能か否か */
  multiple: boolean;
  /** 必須入力か否か */
  required: boolean;
}

export interface QuestionnaireSelectItemOption {
  /** 画面表示項目名 */
  text: string;
  /** 値 */
  value: string;
}

export interface QuestionnairesPostModel {
  /** アンケートタイトル */
  title: string | null;
  /** アンケート説明文 */
  text: string | null;
  /** 説明文用の添付ファイル */
  fileUrls: Array<FileUrl>;
  /** 匿名にするか否か */
  hidden: boolean;
  /** メッセージタイトル */
  messageTitle: string | null;
  /** メッセージ本文 */
  messageText: string | null;
  /** 宛先 */
  targets: string[];
  /** 宛先(LINE) */
  lineTargets: string[];
  /** 宛先(MAIL) */
  mailTargets: string[];
  /** 宛先グループ情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo>;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo>;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<OtherUserGroupInfo>;
  /** 宛先種別 */
  targetType: TargetType | null;
  /** アンケート内容 */
  questions: Array<QuestionTemplateItem>;
  /** 期日 */
  replyLimit: number | null;
  /** 共有一般ユーザーID */
  shareStandardUsers: string[];

  /** メッセージ送信するか否か */
  needSendMessage: boolean;
}

export interface QuestionnairesPatchModel {
  /** アンケートタイトル */
  title: string;
  /** アンケート説明文 */
  text: string;
  /** 説明文用の添付ファイル */
  fileUrls: Array<FileUrl>;
  /** 匿名にするか否か */
  hidden: boolean;
  /** メッセージタイトル */
  messageTitle: string;
  /** メッセージ本文 */
  messageText: string;
  /** 宛先 */
  targets: string[];
  /** 宛先(LINE) */
  lineTargets: string[];
  /** 宛先(MAIL) */
  mailTargets: string[];
  /** 宛先グループ情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo>;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo>;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<OtherUserGroupInfo>;
  /** 宛先種別 */
  targetType: TargetType;
  /** アンケート内容 */
  questions: Array<QuestionTemplateItem>;
  /** 回答受付中か否か */
  acceptingAnswers: boolean;
  /** 期日 */
  replyLimit: number | null;
  /** 共有一般ユーザーID */
  shareStandardUsers: string[];

  /** メッセージ送信するか否か */
  needSendMessage: boolean;
  /** 更新日時 */
  updated: number;
}

export interface QuestionnaireAnswersGetModel {
  /** 施設ID */
  facilityId: number;
  /** ソートキー({parentQuestionnairesSortKey}_{targetSortKey}) */
  questionnaireAnswersSortKey: string;
  /** QuestionnaireID */
  parentQuestionnairesSortKey: number;
  /** 家族または施設関係者を特定するソートキー */
  targetSortKey: string;
  /** 宛先種別 */
  targetType: TargetType;
  /** アンケート回答 */
  answers: Array<QuestionTemplateItem>;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface ResendQuestionnairesPostModel {
  /** メッセージタイトル */
  messageTitle: string;
  /** メッセージ本文 */
  messageText: string;
  /** 宛先 */
  targets: string[];
  /** 宛先(LINE) */
  lineTargets: string[];
  /** 宛先(MAIL) */
  mailTargets: string[];
  /** 宛先グループ情報 */
  sendMessageGroupInfos: Array<SendMessageGroupInfo>;
  /** 利用者グループ情報 */
  residentGroupInfos: Array<ResidentGroupInfo>;
  /** 施設関係者グループ情報 */
  otherUserGroupInfos: Array<OtherUserGroupInfo>;

  /** 更新日時 */
  updated: number;
}
